import {
  faCaretDown,
  faCaretUp,
  IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Children, FC, ReactNode, useMemo, useRef } from 'react';
import { isFragment } from 'react-is';
import {
  ActionMode,
  ActionModeProvider,
  useActionMode,
} from 'src/components/action-mode';
import { Button, ModeButtonProps } from 'src/components/button';
import { useWhileActive } from 'src/hooks/use-while-active';
import { Colors, flyoutStyles } from 'src/styles';
import styled from 'styled-components';

const Container = styled.div`
  position: relative;
  width: max-content;
`;

const Toggle = styled.div`
  display: grid;
  height: 100%;
`;

const IconContainer = styled.span`
  display: grid;
  grid-auto-flow: column;
  grid-gap: 0.5rem;
  align-items: center;
`;

const ToggleIcon = styled.span`
  font-size: 1.2rem;
`;

export const DropdownContent = styled.div`
  display: grid;
  position: absolute;
  right: 0;
  z-index: 1;
  min-width: 17.9rem;
  width: max-content;

  ${flyoutStyles};
`;

export const DropdownItem = styled.div`
  text-align: left;

  padding-top: 0.9rem;
  padding-bottom: 0.9rem;
  border-bottom: 0.1rem solid ${Colors.brandLight3};

  &:last-child {
    border-bottom: none;
  }
`;

export type DropdownProps = {
  label: string;
  initialActive?: boolean;
  'data-testid'?: string;
  children: ReactNode;
} & (
  | {
      icon: IconDefinition;
      showToggleIcon?: boolean;
    }
  | {
      icon?: undefined;
      showToggleIcon?: true;
    }
) &
  ModeButtonProps;

export const Dropdown: FC<DropdownProps> = ({
  icon,
  children,
  'data-testid': dataTestid,
  label,
  initialActive = false,
  showToggleIcon = true,
  ...buttonProps
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useWhileActive(ref, { initialActive });
  const toggleActionMode = useActionMode();
  const actionMode = useMemo<ActionMode>(() => ({ mode: 'dropdown' }), []);

  return (
    <Container ref={ref}>
      <Toggle>
        <Button
          mode="icon-inverse"
          {...toggleActionMode}
          {...buttonProps}
          onClick={() => setActive(!active)}
          aria-expanded={active}
          data-testid={dataTestid}
          aria-label={label}
        >
          <IconContainer>
            {icon && (
              <span>
                <FontAwesomeIcon icon={icon} />
              </span>
            )}
            {showToggleIcon ? (
              <ToggleIcon>
                <FontAwesomeIcon
                  icon={active ? faCaretUp : faCaretDown}
                  aria-hidden="true"
                />
              </ToggleIcon>
            ) : null}
          </IconContainer>
        </Button>
      </Toggle>

      {active && (
        <DropdownContent data-testid="dropdown-content">
          <ActionModeProvider value={actionMode}>
            {Children.map(children, function recursive(child): ReactNode {
              if (isFragment(child)) {
                return Children.map(child.props.children, recursive);
              } else if (child) {
                return <DropdownItem>{child}</DropdownItem>;
              } else {
                return null;
              }
            })}
          </ActionModeProvider>
        </DropdownContent>
      )}
    </Container>
  );
};
